import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import ContentWrapper from '../components/contentwrapper/ContentWrapper';
import { Link, useNavigate } from 'react-router-dom';
import { addFormData, fetchAllDataAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { FaFilter, FaRedo, FaRegEye, FaSyncAlt } from 'react-icons/fa';
import { CiExport } from 'react-icons/ci';
import CsvDownloader from 'react-csv-downloader';
import { numberFormat } from '../components/numberFormat';
import { MdCancel, MdDoneAll, MdLocalShipping, MdNewLabel, MdPendingActions } from 'react-icons/md';
import withAuth from '../utils/withAuth';

const STATUS_CONFIG = {
    'Delivered': { color: '#0e7c52', bg: '#e8f5e9' },
    'RTO Delivered': { color: '#597e0d', bg: '#f1f8e9' },
    'In Transit': { color: '#1565c0', bg: '#e3f2fd' },
    'Out For Delivery': { color: '#00838f', bg: '#e0f7fa' },
    'Cancelled': { color: '#c62828', bg: '#ffebee' },
    'Failed Delivery': { color: '#c62828', bg: '#ffebee' },
    'NEW': { color: '#6a1b9a', bg: '#f3e5f5' },
    'Undelivered': { color: '#e65100', bg: '#fff3e0' },
    'Pickup Booked': { color: '#4e9191', bg: '#e0f4f4' },
    default: { color: '#555', bg: '#f5f5f5' },
};

const CONFIRM_CONFIG = {
    Confirm: { color: '#0e7c52', bg: '#e8f5e9' },
    confirm: { color: '#0e7c52', bg: '#e8f5e9' },
    Confirmed: { color: '#0e7c52', bg: '#e8f5e9' },
    'Confirm Order': { color: '#0e7c52', bg: '#e8f5e9' },
    Pending: { color: '#e65100', bg: '#fff3e0' },
    default: { color: '#555', bg: '#f5f5f5' },
};

const ORDER_TYPES = [
    { key: 'all', label: 'All Orders', icon: <MdLocalShipping />, color: '#4e9191' },
    { key: 'new', label: 'New', icon: <MdNewLabel />, color: '#6a1b9a' },
    { key: 'delivered', label: 'Delivered', icon: <MdDoneAll />, color: '#0e7c52' },
    { key: 'process', label: 'Processing', icon: <MdPendingActions />, color: '#1565c0' },
    { key: 'cancelled', label: 'Cancelled', icon: <MdCancel />, color: '#c62828' },
];

const SHIPPING_STATUSES = [
    'NEW', 'Pickup Booked', 'Pickup Generated', 'Picklist Generated', 'PICKUP SCHEDULED',
    'Out For Pickup', 'Pickup Rescheduled', 'Pickup Exception', 'Box Packing', 'Ready To Pack',
    'In Flight', 'In Transit', 'Out For Delivery', 'Delivered', 'Undelivered', 'Failed Delivery',
    'Delayed', 'RTO Initiated', 'RTO Acknowledged', 'RTO Delivered', 'Cancelled', 'QC FAILED',
    'Pickup Error', 'Returned',
];

const getStatusStyle = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.default;
const getConfirmStyle = (status) => CONFIRM_CONFIG[status] || CONFIRM_CONFIG.default;

const SkeletonRow = () => (
    <tr>
        {[...Array(16)].map((_, i) => (
            <td key={i}>
                <div style={{
                    height: '14px',
                    borderRadius: '6px',
                    background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite'
                }} />
            </td>
        ))}
    </tr>
);

const SatkartarOrders = () => {
    const ref = useRef();
    const navigate = useNavigate();
    const subagentid = localStorage.getItem('subagentdata');
    const [cmsorder, setCmsorder] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [filtervalue, setFilterValue] = useState({});
    const [activeType, setActiveType] = useState('all');
    const [totalCounts, setTotalCounts] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const recordPerPage = 100;

    const filteredOrders = cmsorder.filter((order) => {
        const status = order?.shipment_status;
        if (activeType === 'new') return !status || status === 'NEW';
        if (activeType === 'delivered') return ['Delivered', 'RTO Delivered'].includes(status);
        if (activeType === 'process') return ['In Transit', 'Out For Delivery', 'Pickup Booked', 'RTO Initiated', 'Pickup Rescheduled', 'Out For Pickup', 'Delayed', 'In Flight', 'Box Packing', 'Ready To Pack', 'PICKUP SCHEDULED', 'Pickup Generated', 'Picklist Generated'].includes(status);
        if (activeType === 'cancelled') return ['Cancelled', 'Failed Delivery', 'Returned'].includes(status);
        return true;
    });

    const lastIndex = currentPage * recordPerPage;
    const firstIndex = lastIndex - recordPerPage;
    const records = filteredOrders.slice(firstIndex, lastIndex);
    const npage = Math.ceil(filteredOrders.length / recordPerPage);
    const numbers = [...Array(npage + 1).keys()].slice(1);

    useEffect(() => {
        if (subagentid) {
            getSatkartarOrders();
            getOrderCounts();
        } else {
            navigate('/');
        }
    }, [subagentid]);

    const getSatkartarOrders = () => {
        setIsLoading(true);
        fetchAllDataAPI('sub-agent-satkartar-orders')
            .then((res) => {
                if (res?.data?.status === 1) {
                    setCmsorder(res.data.orders || []);
                    setCurrentPage(1);
                } else {
                    setCmsorder([]);
                    toast.warning('No orders found');
                }
            })
            .catch(() => {
                setCmsorder([]);
                toast.error('Failed to load orders');
            })
            .finally(() => setIsLoading(false));
    };

    const getOrderCounts = () => {
        fetchAllDataAPI('sub-agent-satkartar-counts')
            .then((res) => {
                if (res?.data?.status === 1) setTotalCounts(res.data.counts || {});
            })
            .catch(() => { });
    };

    const syncShopifyOrders = () => {
        setIsSyncing(true);
        addFormData('sub-agent-satkartar-sync-shopify-orders', {})
            .then((res) => {
                if (res?.data?.status === 1) {
                    toast.success(res.data.message || 'Shopify orders synced');
                    getSatkartarOrders();
                    getOrderCounts();
                } else {
                    toast.error(res?.data?.message || 'Sync failed');
                }
            })
            .catch(() => toast.error('Sync failed'))
            .finally(() => setIsSyncing(false));
    };

    const handleFilter = (e) => {
        e.preventDefault();
        if (filtervalue && Object.keys(filtervalue).length > 0) {
            setIsLoading(true);
            addFormData('sub-agent-satkartar-filter', filtervalue)
                .then((res) => {
                    if (res?.data?.status === 1) {
                        setCmsorder(res.data.orders || []);
                        setCurrentPage(1);
                    }
                })
                .catch(() => toast.error('Filter failed'))
                .finally(() => setIsLoading(false));
        } else {
            getSatkartarOrders();
        }
    };

    const resetOrder = () => {
        ref.current.reset();
        setFilterValue({});
        getSatkartarOrders();
    };

    const handleInputs = (e) => {
        setFilterValue({ ...filtervalue, [e.target.name]: e.target.value });
    };

    const columns = [
        { id: 'order_number', displayName: 'Order No.' },
        { id: 'cust_name', displayName: 'Name' },
        { id: 'cust_phone', displayName: 'Mobile' },
        { id: 'subtotal_price', displayName: 'Total Price' },
        { id: 'created_date', displayName: 'Order Date' },
        { id: 'created_time', displayName: 'Order Time' },
        { id: 'state', displayName: 'State' },
        { id: 'city', displayName: 'City' },
        { id: 'pincode', displayName: 'Pincode' },
        { id: 'vendor_name', displayName: 'Platform Name' },
        { id: 'shipingid', displayName: 'Shipping ID' },
        { id: 'awb_number', displayName: 'AWB ID' },
        { id: 'shipment_status', displayName: 'Shipment Status' },
        { id: 'payment_method', displayName: 'Payment Method' },
    ];

    function prevPage() { if (currentPage !== 1) setCurrentPage(currentPage - 1); }
    function changePage(id) { setCurrentPage(id); }
    function nextPage() { if (currentPage !== npage) setCurrentPage(currentPage + 1); }

    return (
        <div className="misDashboard">
            <Helmet><title>SATKARTAR:SUB AGENT::SATKARTAR ORDERS</title></Helmet>
            <style>{`
                @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
                .order-page { animation: fadeInUp 0.4s ease; }
                .order-type-tabs { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 18px; }
                .order-tab { display: flex; align-items: center; gap: 8px; padding: 9px 18px; border-radius: 50px; border: 2px solid #eee; background: #fff; cursor: pointer; font-size: 13px; font-weight: 600; transition: all 0.2s; box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
                .order-tab:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,0.1); }
                .order-tab.active { color: #fff !important; border-color: transparent; }
                .order-tab .tab-count { background: rgba(255,255,255,0.25); padding: 1px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; }
                .order-tab:not(.active) .tab-count { background: #f0f0f0; color: #555; }
                .filter-card { background: #fff; border-radius: 16px; padding: 18px 20px; margin-bottom: 18px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: none; }
                .filter-card label { font-size: 11px; font-weight: 600; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px; display: block; }
                .filter-card .form-control { border-radius: 10px; border: 1.5px solid #eee; font-size: 13px; padding: 8px 12px; transition: border-color 0.2s; }
                .filter-card .form-control:focus { border-color: #4e9191; box-shadow: 0 0 0 3px rgba(78,145,145,0.1); }
                .orders-table-card { background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); overflow: hidden; border: none; }
                .orders-table-card table { margin: 0; }
                .orders-table-card thead th { background: #f8f9fa; font-size: 11px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.5px; padding: 13px 12px; border-bottom: 2px solid #eee; white-space: nowrap; }
                .orders-table-card tbody tr { transition: background 0.15s; border-bottom: 1px solid #f8f8f8; }
                .orders-table-card tbody tr:hover { background: #f8fffe; }
                .orders-table-card tbody td { font-size: 13px; color: #444; padding: 11px 12px; vertical-align: middle; white-space: nowrap; }
                .orders-table-card tfoot th { background: #f8f9fa; font-size: 11px; font-weight: 700; color: #888; padding: 11px 12px; text-transform: uppercase; letter-spacing: 0.5px; }
                .status-badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; }
                .order-num { font-size: 12px; font-weight: 700; color: #4e9191; font-family: monospace; }
                .action-btn { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 8px; margin: 0 2px; transition: all 0.2s; text-decoration: none; }
                .action-btn:hover { transform: translateY(-2px); }
                .action-btn.view { background: #e8f5e9; color: #0e7c52; }
                .round-pagination .page-item.actives .page-link { background-color: #04152d !important; border-color: #04152d !important; color: #fff !important; z-index: 1; border-radius: 8px !important; }
                .round-pagination .page-item .page-link { background-color: #fff; border: 1.5px solid #eee; color: #555; border-radius: 8px !important; margin: 0 2px; font-size: 12px; font-weight: 600; padding: 5px 12px; transition: all 0.2s; box-shadow: none !important; }
                .round-pagination .page-item .page-link:hover { background-color: #04152d !important; border-color: #04152d !important; color: #fff !important; border-radius: 8px !important; }
                .summary-bar { display: flex; align-items: center; justify-content: space-between; padding: 12px 20px; border-bottom: 1px solid #f0f0f0; background: #fafafa; }
                .summary-bar span { font-size: 13px; color: #666; font-weight: 500; }
                .summary-bar strong { color: #333; }
            `}</style>

            <ContentWrapper>
                <div className="order-page">
                    <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
                                <li className="breadcrumb-item">Satkartar Orders</li>
                            </ol>
                        </nav>
                    </div>

                    <div className="order-type-tabs">
                        {ORDER_TYPES.map((type) => (
                            <div
                                key={type.key}
                                className={`order-tab ${activeType === type.key ? 'active' : ''}`}
                                style={activeType === type.key ? { background: type.color, color: '#fff' } : { color: type.color }}
                                onClick={() => { setActiveType(type.key); setCurrentPage(1); }}
                            >
                                {type.icon}
                                {type.label}
                                <span className="tab-count">{totalCounts[type.key] ?? '-'}</span>
                            </div>
                        ))}
                    </div>

                    <div className="filter-card">
                        <form onSubmit={handleFilter} ref={ref}>
                            <div className="row g-2 align-items-end">
                                <div className="col-12 col-lg-2">
                                    <label>Search By Name</label>
                                    <input name="cname" className="form-control" type="text" placeholder="Customer name" onChange={handleInputs} />
                                </div>
                                <div className="col-12 col-lg-2">
                                    <label>Order Number</label>
                                    <input name="onumber" className="form-control" type="text" placeholder="Order number" onChange={handleInputs} />
                                </div>
                                <div className="col-12 col-lg-2">
                                    <label>Mobile Number</label>
                                    <input name="cmobile" className="form-control" type="text" placeholder="Mobile number" onChange={handleInputs} />
                                </div>
                                <div className="col-12 col-lg-2">
                                    <label>Order Status</label>
                                    <select name="sstatus" className="form-control" onChange={handleInputs}>
                                        <option value="">All Statuses</option>
                                        {SHIPPING_STATUSES.map((status) => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12 col-lg-2">
                                    <label>Order Date</label>
                                    <input name="odate" className="form-control" type="date" onChange={handleInputs} />
                                </div>
                                <div className="col-12 col-lg-2">
                                    <div className="d-flex gap-2 align-items-center" style={{ paddingTop: '22px' }}>
                                        <button type="submit" className="btn btn-sm" style={{ background: '#4e9191', color: '#fff', borderRadius: '8px', fontWeight: 600, fontSize: '12px', padding: '7px 14px', whiteSpace: 'nowrap' }}>
                                            <FaFilter style={{ marginRight: '4px', fontSize: '10px' }} /> Filter
                                        </button>
                                        <button type="button" onClick={resetOrder} className="btn btn-sm" style={{ background: '#fff3cd', color: '#856404', borderRadius: '8px', fontWeight: 600, fontSize: '12px', padding: '7px 10px' }}>
                                            <FaRedo style={{ fontSize: '11px' }} />
                                        </button>
                                        <CsvDownloader filename="Satkartar Orders" extension=".csv" datas={cmsorder} columns={columns}>
                                            <button type="button" className="btn btn-sm" style={{ background: '#e3f2fd', color: '#1565c0', borderRadius: '8px', fontWeight: 600, fontSize: '12px', padding: '7px 10px' }}>
                                                <CiExport style={{ fontSize: '14px' }} />
                                            </button>
                                        </CsvDownloader>
                                        <button type="button" onClick={syncShopifyOrders} disabled={isSyncing} className="btn btn-sm" style={{ background: '#eef7f7', color: '#0d4f4f', borderRadius: '8px', fontWeight: 600, fontSize: '12px', padding: '7px 10px' }}>
                                            <FaSyncAlt style={{ fontSize: '12px' }} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="orders-table-card" style={{ marginBottom: '50px' }}>
                        <div className="summary-bar">
                            <span>
                                Showing <strong>{records.length}</strong> of <strong>{filteredOrders.length}</strong> orders
                                {activeType !== 'all' && (
                                    <span style={{ marginLeft: '8px', color: '#4e9191', fontWeight: 600, textTransform: 'capitalize' }}>
                                        - {activeType}
                                    </span>
                                )}
                            </span>
                            <span>Page <strong>{currentPage}</strong> of <strong>{npage || 1}</strong></span>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Order No.</th>
                                        <th>AWB Number</th>
                                        <th>CRM Order No</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Price</th>
                                        <th>Order Date</th>
                                        <th>Order Time</th>
                                        <th>State</th>
                                        <th>City</th>
                                        <th>Pincode</th>
                                        <th>Order Type</th>
                                        <th>Status</th>
                                        <th>Payment Method</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading
                                        ? [...Array(10)].map((_, i) => <SkeletonRow key={i} />)
                                        : records.length === 0
                                            ? (
                                                <tr>
                                                    <td colSpan={16} className="text-center py-5">
                                                        <div style={{ color: '#bbb', fontSize: '14px' }}>No orders found</div>
                                                    </td>
                                                </tr>
                                            )
                                            : records.map((record, index) => {
                                                const statusStyle = getStatusStyle(record?.shipment_status);
                                                const confirmStyle = getConfirmStyle(record?.order_confirm_status);
                                                return (
                                                    <tr key={index}>
                                                        <td style={{ color: '#aaa', fontSize: '12px' }}>{firstIndex + index + 1}</td>
                                                        <td><span className="order-num">#{record?.order_number}</span></td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#888' }}>{record?.awb_number || '-'}</td>
                                                        <td>
                                                            <span style={{ color: '#1b9a69', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>
                                                                {record?.crm_order_id || '-'}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontWeight: 500 }}>{record?.cust_name}</td>
                                                        <td>{record?.cust_phone}</td>
                                                        <td style={{ fontWeight: 600, color: '#0e7c52' }}>{numberFormat(record?.subtotal_price)}</td>
                                                        <td>{record?.created_date}</td>
                                                        <td style={{ color: '#888' }}>{record?.created_time}</td>
                                                        <td>{record?.state}</td>
                                                        <td>{record?.city}</td>
                                                        <td>{record?.pincode}</td>
                                                        <td>
                                                            <span style={{ background: confirmStyle.bg, color: confirmStyle.color, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, display: 'inline-block' }}>
                                                                {record?.order_confirm_status || '-'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="status-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                                                                {record?.shipment_status || 'New'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="status-badge" style={{ background: '#f5f5f5', color: '#555' }}>
                                                                {record?.payment_method || '-'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <Link style={{ cursor: 'pointer' }} to={`/order-detail/${record?.order_number || record?.order_id}`} className="action-btn view">
                                                                <FaRegEye style={{ fontSize: '14px' }} />
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th>#</th>
                                        <th>Order No.</th>
                                        <th>AWB Number</th>
                                        <th>CRM Order No</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Price</th>
                                        <th>Order Date</th>
                                        <th>Order Time</th>
                                        <th>State</th>
                                        <th>City</th>
                                        <th>Pincode</th>
                                        <th>Order Type</th>
                                        <th>Status</th>
                                        <th>Payment Method</th>
                                        <th>Action</th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="d-flex justify-content-end p-3">
                            <nav>
                                <ul className="pagination round-pagination mb-0">
                                    <li className="page-item">
                                        <a href="#" className="page-link" onClick={(e) => { e.preventDefault(); prevPage(); }}>Prev</a>
                                    </li>
                                    {numbers.slice(Math.max(0, currentPage - 2), Math.max(5, currentPage + 3)).map((n, i) => (
                                        <li key={i} className={`page-item ${currentPage === n ? 'actives' : ''}`}>
                                            <a href="#" className="page-link" onClick={(e) => { e.preventDefault(); changePage(n); }}>{n}</a>
                                        </li>
                                    ))}
                                    <li className="page-item">
                                        <a href="#" className="page-link" onClick={(e) => { e.preventDefault(); nextPage(); }}>Next</a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(SatkartarOrders);
